import { Controller, useFormContext } from "react-hook-form"
import {
  DualColumnRow,
  SingleColumnRow,
  Subtitle,
} from "../../../../../../style"
import { Trans, t } from "@lingui/macro"

import { BillingData } from "../../../../types"
import { DropdownField } from "../../../../../../../../../../components/DropdownField"
import { IdentityNumberController } from "../../../../../IdentityNumberController"
import { IdentityType } from "../../../../../../../../../../api/stores/checkout"
import { PostalCodeController } from "../../../../../PostalCodeController"
import { TextField } from "../../../../../../../../../../components/TextField"
import { getLocaleStates } from "../../../../../../../../../../utils/getLocaleStates"
import { i18n } from "@lingui/core"
import { useCheckout } from "../../../../../../../../../../hooks"
import { useEffect } from "react"

type Option = { id: string; name: string; value: IdentityType }
const arrayOfAllOptions: ArrayOfAllUnion<Option> = array => array

export const BillingForm = () => {
  const { billing } = useCheckout().data.meta
  const {
    control,
    errors,
    setValue,
    watch,
    getValues,
  } = useFormContext<BillingData>()

  useEffect(() => {
    //set billing from api
    const isBillingEmpty = billing == null || billing?.fullName === ""

    if (!isBillingEmpty) {
      setValue("billingFullName", billing?.fullName || "")
      setValue("billingIdentityNumber", billing?.identityNumber || "")
      setValue("billingIdentityType", billing?.identityNumber || "")
      setValue("billingAddressStreet", billing?.addressStreet || "")
      setValue("billingAddressNumber", billing?.addressNumber || "")
      setValue("billingAddressAdditional", billing?.addressOther || "")
      setValue("billingCity", billing?.city || "")
      setValue("billingPostalCode", billing?.postalCode || "")
      setValue("billingState", billing?.state || "")
    }
  }, [billing, setValue])

  return (
    <SingleColumnRow>
      <Subtitle>
        <Trans>Datos de la persona que paga</Trans>
      </Subtitle>

      <Controller
        control={control}
        name="billingFullName"
        defaultValue=""
        rules={{
          required: t`Completá el campo nombre y apellido/razón social.`,
        }}
        render={({ onChange, onBlur, value, name }) => (
          <TextField
            name={name}
            label={<Trans>Nombre y apellido/Razón social</Trans>}
            errorMsg={errors.billingFullName?.message}
            maxLength={100}
            hideLength={true}
            value={value}
            onBlur={onBlur}
            onChange={onChange}
          />
        )}
      />
      <DualColumnRow>
        <Controller
          control={control}
          name="billingIdentityType"
          defaultValue={i18n.locale === "es-MX" ? "OTHER" : "DU"}
          rules={{
            required: t`Elegí el tipo de documento.`,
          }}
          render={({ onChange, value }) => (
            <DropdownField
              name="billingIdentityType"
              label={<Trans>Tipo de documento</Trans>}
              errorMsg={errors.billingIdentityType?.message}
              options={arrayOfAllOptions(
                i18n.locale === "es-MX"
                  ? [
                      {
                        id: "3",
                        name: t`Otro`,
                        value: "OTHER",
                      },
                      {
                        id: "1",
                        name: t`DNI`,
                        value: "DU",
                      },
                      {
                        id: "2",
                        name: t`CUIT`,
                        value: "CUIT",
                      },
                    ]
                  : [
                      {
                        id: "1",
                        name: t`DNI`,
                        value: "DU",
                      },
                      {
                        id: "2",
                        name: t`CUIT`,
                        value: "CUIT",
                      },
                      {
                        id: "3",
                        name: t`Otro`,
                        value: "OTHER",
                      },
                    ]
              )}
              value={value}
              onChange={onChange}
            />
          )}
        />
        <IdentityNumberController
          name="billingIdentityNumber"
          errorMsg={errors.billingIdentityNumber?.message}
          control={control}
          selectedIdentityType={watch("billingIdentityType") as IdentityType}
          defaultValue={getValues("billingIdentityNumber")}
        />
      </DualColumnRow>

      <Controller
        control={control}
        name="billingAddressStreet"
        defaultValue=""
        rules={{
          required: t`Completá el campo calle.`,
        }}
        render={({ onChange, onBlur, value, name }) => (
          <TextField
            name={name}
            label={<Trans>Calle (sin número)</Trans>}
            errorMsg={errors.billingAddressStreet?.message}
            maxLength={100}
            hideLength={true}
            value={value}
            onBlur={onBlur}
            onChange={onChange}
          />
        )}
      />

      <DualColumnRow>
        <Controller
          control={control}
          name="billingAddressNumber"
          defaultValue=""
          rules={{
            required: t`Completá el campo número.`,
          }}
          render={({ onChange, onBlur, value, name }) => (
            <TextField
              name={name}
              label={<Trans>Número</Trans>}
              errorMsg={errors.billingAddressNumber?.message}
              maxLength={10}
              hideLength={true}
              onBlur={onBlur}
              onChange={onChange}
              value={value?.replace(/[^0-9]/g, "")}
            />
          )}
        />

        <Controller
          control={control}
          name="billingAddressAdditional"
          defaultValue=""
          render={({ onChange, onBlur, value, name }) => (
            <TextField
              name={name}
              label={<Trans>Departamento (opcional)</Trans>}
              errorMsg={errors.billingAddressAdditional?.message}
              maxLength={100}
              hideLength={true}
              value={value}
              onBlur={onBlur}
              onChange={onChange}
            />
          )}
        />
      </DualColumnRow>

      <Controller
        control={control}
        name="billingCity"
        defaultValue=""
        rules={{
          required: t`Completá el campo ciudad.`,
        }}
        render={({ onChange, onBlur, value, name }) => (
          <TextField
            name={name}
            label={<Trans>Ciudad</Trans>}
            errorMsg={errors.billingCity?.message}
            maxLength={100}
            value={value}
            onBlur={onBlur}
            onChange={onChange}
            hideLength={true}
          />
        )}
      />

      <DualColumnRow>
        <PostalCodeController
          name="billingPostalCode"
          errorMsg={errors.billingPostalCode?.message}
          control={control}
          defaultValue={getValues("billingPostalCode")}
        />
      </DualColumnRow>

      <Controller
        control={control}
        name="billingState"
        defaultValue=""
        rules={{
          required: t`Elegí la provincia.`,
        }}
        render={({ name, value, onChange }) => (
          <DropdownField
            name={name}
            label={<Trans>Provincia</Trans>}
            errorMsg={errors.billingState?.message}
            options={getLocaleStates()}
            dataTestId={"billing-state-dropdown"}
            value={value}
            onChange={onChange}
          />
        )}
      />
    </SingleColumnRow>
  )
}
